import { useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { comboLabel, type Combination } from '../../lib/refdata';
import type { FeeScheduleRow, Page, SeyPayRefund, SeyPayRevenueAnalytics, SeyPaySettlementBatch } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

const REFUND_TABS = ['REQUESTED', 'APPROVED', 'REJECTED', 'PAID'] as const;

export function SeyPay() {
  const { persona } = useAuth();
  const isFeeAdmin = persona?.roles.includes('FEE_ADMIN') ?? false;
  const isFinanceOfficer = persona?.roles.includes('FINANCE_OFFICER') ?? false;
  const isRefundApprover = persona?.roles.includes('REFUND_APPROVER') ?? false;

  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [showFee, setShowFee] = useState(false);
  const [showRun, setShowRun] = useState(false);
  const [showRequestRefund, setShowRequestRefund] = useState(false);
  const [refundTab, setRefundTab] = useState<(typeof REFUND_TABS)[number]>('REQUESTED');
  const [actionError, setActionError] = useState<string | null>(null);

  const analyticsQ = useApiQuery<SeyPayRevenueAnalytics>(
    isFinanceOfficer || isFeeAdmin ? `/api/v1/pay/analytics/revenue?period=${period}` : null,
  );
  const feesQ = useApiQuery<Page<FeeScheduleRow>>('/api/v1/pay/fees?limit=100');
  const settlementsQ = useApiQuery<Page<SeyPaySettlementBatch>>(isFinanceOfficer ? '/api/v1/pay/settlements?limit=100' : null);
  const refundsQ = useApiQuery<Page<SeyPayRefund>>(
    isFinanceOfficer || isRefundApprover ? `/api/v1/pay/refunds?status=${refundTab}&limit=100` : null,
  );

  const reconcile = async (batch: SeyPaySettlementBatch) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/pay/settlements/${batch.id}/reconcile`);
      settlementsQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Reconcile failed');
    }
  };

  const approveRefund = async (r: SeyPayRefund) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/pay/refunds/${r.id}/approve`);
      refundsQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Approval failed');
    }
  };

  const [rejectTarget, setRejectTarget] = useState<SeyPayRefund | null>(null);

  return (
    <div>
      <PageHeader
        title="SeyPay & Settlements"
        subtitle="Treasury back office for the government e-payments engine — fee schedule, nightly settlement, refund approvals"
        actions={
          isFeeAdmin ? (
            <button className="btn-primary" onClick={() => setShowFee(true)}>
              + Fee schedule row
            </button>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {(isFinanceOfficer || isFeeAdmin) && (
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Collections analytics</h2>
            <input className="field-input w-36 !py-1 text-xs" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </div>
          {analyticsQ.loading ? (
            <Spinner />
          ) : analyticsQ.error ? (
            <ErrorNote message={analyticsQ.error} />
          ) : analyticsQ.data ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Total collected" value={scr(analyticsQ.data.total)} hint={`${analyticsQ.data.count} transaction(s)`} />
              {Object.entries(analyticsQ.data.byMda)
                .slice(0, 3)
                .map(([mda, amt]) => (
                  <StatCard key={mda} label={mda} value={scr(amt)} />
                ))}
            </div>
          ) : null}
        </div>
      )}

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Fee schedule</h2>
        <div className="card overflow-x-auto">
          {feesQ.loading ? (
            <Spinner />
          ) : feesQ.error ? (
            <ErrorNote message={feesQ.error} />
          ) : (feesQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No fees scheduled" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">MDA / Service</th>
                  <th className="table-head">Name</th>
                  <th className="table-head text-right">Amount</th>
                  <th className="table-head">Effective</th>
                  {isFeeAdmin ? <th className="table-head" /> : null}
                </tr>
              </thead>
              <tbody>
                {feesQ.data!.items.map((f) => (
                  <tr key={f.id}>
                    <td className="table-cell text-xs">
                      {f.mdaCode} / {f.serviceCode}
                    </td>
                    <td className="table-cell">
                      {f.name}
                      {f.vatFlag ? <span className="ml-1 text-[10px] text-slate-400">VAT</span> : null}
                    </td>
                    <td className="table-cell text-right tabular-nums">
                      {f.currency} {scr(f.amount)}
                    </td>
                    <td className="table-cell text-xs text-slate-500">
                      {formatDate(f.validFrom)} {f.validTo ? `→ ${formatDate(f.validTo)}` : '→ open'}
                    </td>
                    {isFeeAdmin ? (
                      <td className="table-cell text-right">
                        {!f.validTo ? (
                          <button
                            className="btn-secondary !px-2.5 !py-1 text-xs"
                            onClick={async () => {
                              const validTo = window.prompt('Retire this fee row effective (YYYY-MM-DD):', new Date().toISOString().slice(0, 10));
                              if (!validTo) return;
                              await api.post(`/api/v1/pay/fees/${f.id}/retire`, { validTo });
                              feesQ.reload();
                            }}
                          >
                            Retire
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {isFinanceOfficer ? (
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Nightly settlement batches</h2>
            <button className="btn-secondary !py-1 text-xs" onClick={() => setShowRun(true)}>
              Run settlement for a date
            </button>
          </div>
          <div className="card overflow-x-auto">
            {settlementsQ.loading ? (
              <Spinner />
            ) : settlementsQ.error ? (
              <ErrorNote message={settlementsQ.error} />
            ) : (settlementsQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No settlement batches yet" hint="Run the nightly settlement for a collection day to see batches here." />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Batch</th>
                    <th className="table-head">MDA</th>
                    <th className="table-head">Date</th>
                    <th className="table-head text-right">Collected</th>
                    <th className="table-head">Reconciled</th>
                    <th className="table-head" />
                  </tr>
                </thead>
                <tbody>
                  {settlementsQ.data!.items.map((b) => (
                    <tr key={b.id}>
                      <td className="table-cell font-medium">{b.batchNo}</td>
                      <td className="table-cell text-xs">{b.mdaCode}</td>
                      <td className="table-cell text-slate-500">{formatDate(b.date)}</td>
                      <td className="table-cell text-right tabular-nums">{scr(b.totalCollected)}</td>
                      <td className="table-cell">
                        <StatusBadge status={b.reconciled ? 'RECONCILED' : 'PENDING'} />
                      </td>
                      <td className="table-cell text-right">
                        {!b.reconciled ? (
                          <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => reconcile(b)}>
                            Reconcile
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      {isFinanceOfficer || isRefundApprover ? (
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Refund approval queue</h2>
            {isFinanceOfficer ? (
              <button className="btn-secondary !py-1 text-xs" onClick={() => setShowRequestRefund(true)}>
                + Request refund
              </button>
            ) : null}
          </div>
          <div className="mb-3 flex gap-1 rounded-xl bg-slate-200/60 p-1">
            {REFUND_TABS.map((t) => (
              <button
                key={t}
                onClick={() => setRefundTab(t)}
                className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                  refundTab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="card overflow-x-auto">
            {refundsQ.loading ? (
              <Spinner />
            ) : refundsQ.error ? (
              <ErrorNote message={refundsQ.error} />
            ) : (refundsQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="Nothing here" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Intent</th>
                    <th className="table-head text-right">Amount</th>
                    <th className="table-head">Reason</th>
                    <th className="table-head">Status</th>
                    {isRefundApprover ? <th className="table-head" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {refundsQ.data!.items.map((r) => (
                    <tr key={r.id}>
                      <td className="table-cell text-xs">{r.intentId}</td>
                      <td className="table-cell text-right tabular-nums">{scr(r.amount)}</td>
                      <td className="table-cell max-w-xs truncate text-xs" title={r.reason}>
                        {r.reason}
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={r.status} />
                        {r.status === 'REJECTED' && r.rejectedReason ? <div className="text-[11px] text-rose-600">{r.rejectedReason}</div> : null}
                      </td>
                      {isRefundApprover ? (
                        <td className="table-cell text-right">
                          {r.status === 'REQUESTED' ? (
                            <div className="flex justify-end gap-1.5">
                              <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => approveRefund(r)}>
                                Approve
                              </button>
                              <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setRejectTarget(r)}>
                                Reject
                              </button>
                            </div>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      {showFee ? (
        <FeeModal
          onClose={() => setShowFee(false)}
          onDone={() => {
            setShowFee(false);
            feesQ.reload();
          }}
        />
      ) : null}
      {showRun ? (
        <RunSettlementModal
          onClose={() => setShowRun(false)}
          onDone={() => {
            setShowRun(false);
            settlementsQ.reload();
          }}
        />
      ) : null}
      {showRequestRefund ? (
        <RequestRefundModal
          onClose={() => setShowRequestRefund(false)}
          onDone={() => {
            setShowRequestRefund(false);
            refundsQ.reload();
          }}
        />
      ) : null}
      {rejectTarget ? (
        <RejectRefundModal
          refund={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onDone={() => {
            setRejectTarget(null);
            refundsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function FeeModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const combosQ = useApiQuery<{ items: Combination[] }>('/api/v1/coa/combinations?limit=100');
  const [form, setForm] = useState({
    mdaCode: '',
    serviceCode: '',
    name: '',
    amount: '',
    vatFlag: false,
    legalRef: '',
    coaCombinationId: '',
    validFrom: new Date().toISOString().slice(0, 10),
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/pay/fees', {
        ...form,
        amount: Number.parseFloat(form.amount),
        currency: 'SCR',
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create fee row');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New fee schedule row" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="MDA code">
            <input className="field-input" value={form.mdaCode} onChange={(e) => setForm({ ...form, mdaCode: e.target.value })} placeholder="e.g. MTC" />
          </Field>
          <Field label="Service code">
            <input className="field-input" value={form.serviceCode} onChange={(e) => setForm({ ...form, serviceCode: e.target.value })} placeholder="e.g. TOUR-PERMIT" />
          </Field>
        </div>
        <Field label="Fee name">
          <input className="field-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Tour guide permit" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount (SCR)">
            <input className="field-input" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </Field>
          <Field label="Effective from">
            <input className="field-input" type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} />
          </Field>
        </div>
        <Field label="Budget line (CoA combination)">
          <select className="field-input" value={form.coaCombinationId} onChange={(e) => setForm({ ...form, coaCombinationId: e.target.value })}>
            <option value="">Select…</option>
            {(combosQ.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {comboLabel(c)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Legal reference">
          <input className="field-input" value={form.legalRef} onChange={(e) => setForm({ ...form, legalRef: e.target.value })} placeholder="e.g. Tourism Fees Regulations 2024, s.4" />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.vatFlag} onChange={(e) => setForm({ ...form, vatFlag: e.target.checked })} />
          VAT applies
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !form.mdaCode || !form.serviceCode || !form.name || !form.amount || !form.coaCombinationId || !form.legalRef}
          >
            {busy ? 'Saving…' : 'Create fee row'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RunSettlementModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ batches: unknown[] }>('/api/v1/pay/settlements/run', { date });
      setResult(`${r.batches.length} MDA batch(es) settled for ${date}.`);
      setTimeout(onDone, 900);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Settlement run failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Run nightly settlement" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {result ? <div className="rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{result}</div> : null}
        <Field label="Collection day to settle">
          <input className="field-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Running…' : 'Run settlement'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RequestRefundModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [ref, setRef] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/pay/intents/${ref}/refund`, { amount: Number.parseFloat(amount), reason });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Refund request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Request a refund" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Payment intent reference">
          <input className="field-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. OG-2026-0004821" />
        </Field>
        <Field label="Amount (SCR)">
          <input className="field-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Reason">
          <textarea className="field-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Service could not be delivered — duplicate payment" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !ref || !amount || reason.length < 5}>
            {busy ? 'Requesting…' : 'Request refund'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RejectRefundModal({ refund, onClose, onDone }: { refund: SeyPayRefund; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/pay/refunds/${refund.id}/reject`, { reason });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Rejection failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Reject refund — ${scr(refund.amount)}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Reason">
          <textarea className="field-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.length < 3}>
            {busy ? 'Rejecting…' : 'Reject refund'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
